import React, { useState, useEffect } from "react";
import { authFetch, apiUrl } from "../../../utils/api";
import { useClerk } from "@clerk/clerk-react";

/**
 * GeneralTab - System settings and GDPR controls
 *
 * Features:
 * - Theme selection (dark/light/auto)
 * - Archive conversations management
 * - Export data (JSON)
 * - Delete all data
 */
const GeneralTab = ({ onShowTutorial }) => {
  const [themeMode, setThemeMode] = useState(
    localStorage.getItem("themeMode") || "auto",
  );
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openWalkthrough, setOpenWalkthrough] = useState(null); // inline video embeds
  const { signOut } = useClerk();



  // Handle theme change
  const handleThemeChange = (mode) => {
    setThemeMode(mode);
    localStorage.setItem("themeMode", mode);
    if (window.applyTheme) {
      window.applyTheme(mode);
    }
  };

  // Load archived conversations
  const loadArchivedConversations = async () => {
    try {
      const response = await authFetch(
        apiUrl("/api/chat/conversations?archived=true"),
      );
      const data = await response.json();
      const archived = data.conversations?.filter((c) => c.is_archived) || [];
      setArchivedConversations(archived);
      setShowArchived(true);
    } catch (error) {
      console.error("Failed to load archived conversations:", error);
      alert("❌ Failed to load archived conversations");
    }
  };

  // Restore archived conversation
  const restoreConversation = async (convId) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/chat/conversations/${convId}`),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_archived: 0 }),
        },
      );

      if (response.ok) {
        setArchivedConversations((prev) => prev.filter((c) => c.id !== convId));
        alert("✅ Conversation restored");
      } else {
        alert("❌ Failed to restore conversation");
      }
    } catch (error) {
      console.error("Restore error:", error);
      alert("❌ Failed to restore conversation");
    }
  };

  // Permanently delete conversation
  const permanentlyDelete = async (convId) => {
    if (
      !window.confirm(
        "Permanently delete this conversation? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const response = await authFetch(
        apiUrl(`/api/chat/conversations/${convId}`),
        { method: "DELETE" },
      );

      if (response.ok) {
        setArchivedConversations((prev) => prev.filter((c) => c.id !== convId));
        alert("✅ Conversation permanently deleted");
      } else {
        alert("❌ Failed to delete conversation");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("❌ Failed to delete conversation");
    }
  };

  // Export data - uses GDPR-compliant endpoint that exports ALL user data
  const exportData = async () => {
    setIsExporting(true);
    try {
      // Use the comprehensive GDPR export endpoint
      const response = await authFetch(apiUrl("/api/user/data/export"));

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const data = await response.json();
      const jsonData = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonData], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `optimus-os-full-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert(
        `✅ Full data export complete!\n\nIncludes:\n- ${data.conversations?.length || 0} conversations with messages\n- Settings & preferences\n- Automations\n- ROI metrics\n- Connected services`,
      );
    } catch (error) {
      console.error("Export error:", error);
      alert("❌ Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Clear all data - two-step process for safety
  const clearAllData = async () => {
    // Step 1: First warning
    const confirmed = window.confirm(
      "⚠️ DANGER ZONE ⚠️\n\n" +
      "This will PERMANENTLY DELETE all your data:\n" +
      "• All conversations and messages\n" +
      "• All automations and workflows\n" +
      "• All connected service credentials\n" +
      "• All settings and preferences\n" +
      "• All ROI tracking data\n\n" +
      "THIS ACTION CANNOT BE UNDONE.\n\n" +
      "Are you sure you want to continue?",
    );

    if (!confirmed) return;

    // Step 2: Request deletion token from server (shows what will be deleted)
    setIsDeleting(true);
    try {
      const requestResponse = await authFetch(
        apiUrl("/api/user/data/delete/request"),
        { method: "POST" },
      );

      if (!requestResponse.ok) {
        throw new Error("Failed to initiate deletion");
      }

      const requestData = await requestResponse.json();

      // Step 3: Show exactly what will be deleted and get final confirmation
      const finalConfirm = window.confirm(
        "⚠️ FINAL WARNING - POINT OF NO RETURN ⚠️\n\n" +
        "The following will be permanently deleted:\n" +
        `• ${requestData.data_to_be_deleted?.conversations || 0} conversations\n` +
        `• ${requestData.data_to_be_deleted?.messages || 0} messages\n` +
        `• ${requestData.data_to_be_deleted?.automations || 0} automations\n` +
        `• ${requestData.data_to_be_deleted?.credentials || 0} connected services\n\n` +
        "Type 'DELETE' in the next prompt to confirm.",
      );

      if (!finalConfirm) {
        setIsDeleting(false);
        return;
      }

      // Step 4: Require typing DELETE
      const typeConfirm = window.prompt(
        "To confirm permanent deletion, type exactly: DELETE ALL MY DATA",
      );

      if (typeConfirm !== "DELETE ALL MY DATA") {
        alert("Deletion cancelled - confirmation phrase did not match.");
        setIsDeleting(false);
        return;
      }

      // Step 5: Execute deletion with confirmation token
      const deleteResponse = await authFetch(
        apiUrl("/api/user/data/delete/confirm"),
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            confirmation_token: requestData.confirmation_token,
            confirm_phrase: "DELETE ALL MY DATA",
          }),
        },
      );

      if (deleteResponse.ok) {
        const result = await deleteResponse.json();
        // Clear local storage
        localStorage.clear();
        alert(
          "✅ All data has been permanently deleted.\n\n" +
          `Deleted:\n` +
          `• ${result.deleted?.conversations || 0} conversations\n` +
          `• ${result.deleted?.messages || 0} messages\n` +
          `• ${result.deleted?.automations || 0} automations\n\n` +
          "The page will now reload.",
        );
        window.location.reload();
      } else {
        const error = await deleteResponse.json();
        alert(`❌ Failed to delete data: ${error.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Clear data error:", error);
      alert("❌ Failed to delete data. Please try again or contact support.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="settings-section">
      {/* About Section */}
      <div className="setting-group" style={{ marginBottom: "32px" }}>
        <h3>🦡 About Optimus OS</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px", lineHeight: "1.6" }}>
          Optimus OS is your AI-powered business automation assistant. Connect
          your favorite tools and let the honey badger handle the busy work.
        </p>
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: "rgba(255, 204, 0, 0.1)",
            border: "1px solid rgba(255, 204, 0, 0.3)",
            borderRadius: "8px",
          }}
        >
          <p
            style={{
              color: "#ffcc00",
              fontSize: "13px",
              fontWeight: "500",
              marginBottom: "8px",
            }}
          >
            🧠 A Note About AI
          </p>
          <p
            style={{
              color: "var(--text-secondary)",
              fontSize: "13px",
              lineHeight: "1.5",
              margin: 0,
            }}
          >
            AI is getting smarter all the time, but it doesn't live in 3D
            reality like you do. It might occasionally hallucinate or come to an
            incorrect conclusion. Please double-check important info, trust your
            gut, and verify claims when it matters.
          </p>
        </div>
        <p style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "12px" }}>
          Version 1.1.11 • Made with 🧡 by Optimus OS, Inc
        </p>
      </div>

      {/* Support — mirrors the portal account-page pattern (Brad 2026-09-03:
          support email must be CLEARLY visible in Settings + Clerk dropdown) */}
      <div className="setting-group" style={{ marginBottom: "32px" }}>
        <h3>💬 Support</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "12px" }}>
          Question, feedback, or something broken? Email us — same-day answers,
          usually same-day fixes. Our agents watch the logs too, so many issues
          are fixed before you even write in.
        </p>
        <a
          href="mailto:support@osoptimus.com?subject=Optimus%20OS%20—%20question%20%2F%20feedback%20%2F%20problem"
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            padding: "10px 16px",
            background: "var(--bg-tertiary)",
            border: "1px solid var(--border-primary)",
            borderRadius: "10px",
            color: "var(--accent-primary)",
            fontWeight: 600,
            fontSize: "14px",
            textDecoration: "none",
          }}
        >
          support@osoptimus.com
        </a>
      </div>

      {/* The Operating Manual — the anchor doc from the offer stack */}
      <div className="setting-group" style={{ marginBottom: "32px" }}>
        <h3>📖 The Optimus Operating Manual</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "12px" }}>
          The 60-page anchor document — OSLO, the three surfaces, FAST,
          Coached-OS, and your first thirty days. Hand it to anyone on your
          team.
        </p>
        <a
          href="/docs/optimus-operating-manual.pdf"
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            padding: "10px 16px",
            background: "var(--bg-tertiary)",
            border: "1px solid var(--border-primary)",
            borderRadius: "10px",
            color: "var(--accent-primary)",
            fontWeight: 600,
            fontSize: "14px",
            textDecoration: "none",
          }}
        >
          Open the manual (PDF)
        </a>
      </div>

      {/* Video walkthroughs — real screens, ~1 min each */}
      <div className="setting-group" style={{ marginBottom: "32px" }}>
        <h3>🎬 Video Walkthroughs</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "12px" }}>
          One-minute tours of every major surface — real screens, narrated.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "860px" }}>
          {[
            ["Chat with Ollie — your second brain", "1223784156", "91e5863840"],
            ["Settings tour — the workspace", "1223785254", "e56dd90abc"],
            ["Integrations — connect Claude + your tools", "1223784822", "707b4df012"],
            ["Custom Tools — paste a key, tools build themselves", "1223784439", "abae0ac480"],
            ["Agents — Mako & Orca", "1223783805", "bb0c856772"],
            ["Skills — your library", "1223785560", "d40b4ecd67"],
            ["Build a skill — the wizard, Ollie, and slash commands", "1227089857", "5b72c05a52"],
            ["Skills that learn — run notes and team sharing", "1227090107", "cd2faf8626"],
            ["Custom — everything you built, one hover away", "1227090369", "4c4d573141"],
            ["Ask the coach — Ollie coaches your business", "1227090504", "92f2dd0ce6"],
            ["Custom dashboards — your numbers, one screen", "1227134460", "39d12da06d"],
            ["Harry — the work that runs in the background", "1227134620", "35b339a410"],
            ["Affiliate — get paid for the referrals you already make", "1227134721", "93e2a62b4e"],
            ["Files — your workspace", "1223784594", "faf459f4b7"],
            ["Automations & ROI", "1223784005", "182ff29549"],
            ["Usage & Billing", "1223786184", "b22bc5512c"],
            ["Team workspaces", "1223786038", "3d61338041"],
            ["Getting help — Support", "1223785928", "5114fa1584"],
          ].map(([label, id, hash]) => (
            <div
              key={id}
              style={{
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border-primary)",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => setOpenWalkthrough(openWalkthrough === id ? null : id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "10px 12px",
                  background: "transparent",
                  border: "none",
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span>▶ {label}</span>
                <span style={{ color: "var(--text-tertiary)", fontSize: "11px" }}>
                  {openWalkthrough === id ? "▲" : "▼"}
                </span>
              </button>
              {openWalkthrough === id && (
                <div style={{ position: "relative", paddingTop: "56.25%" }}>
                  <iframe
                    src={`https://player.vimeo.com/video/${id}?h=${hash}&autoplay=1&dnt=1`}
                    title={label}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Start Tutorial */}
      {onShowTutorial && (
        <div className="setting-group" style={{ marginBottom: "32px" }}>
          <h3>📚 Quick Start Tutorial</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "12px" }}>
            New to Optimus? Take a quick tour of the key features.
          </p>
          <button
            onClick={onShowTutorial}
            style={{
              padding: "12px 20px",
              background: "linear-gradient(135deg, #00d4ff 0%, #0095ff 100%)",
              border: "none",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 16px rgba(0, 212, 255, 0.3)",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 212, 255, 0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 212, 255, 0.3)";
            }}
          >
            <span>✨</span> Replay Tutorial
          </button>
        </div>
      )}

      <h3>⚙️ System Settings</h3>

      {/* Theme Selection */}
      <div className="setting-group">
        <label>Theme</label>
        <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
          <div
            onClick={() => handleThemeChange("auto")}
            style={{
              flex: 1,
              padding: "16px",
              border:
                themeMode === "auto"
                  ? "2px solid #00d4ff"
                  : "1px solid var(--border-primary)",
              borderRadius: "8px",
              background: "var(--bg-secondary)",
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.2s ease",
            }}
          >
            🌓 Auto
          </div>
          <div
            onClick={() => handleThemeChange("light")}
            style={{
              flex: 1,
              padding: "16px",
              border:
                themeMode === "light"
                  ? "2px solid #00d4ff"
                  : "1px solid var(--border-primary)",
              borderRadius: "8px",
              background: "var(--bg-secondary)",
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.2s ease",
            }}
          >
            ☀️ Light
          </div>
          <div
            onClick={() => handleThemeChange("dark")}
            style={{
              flex: 1,
              padding: "16px",
              border:
                themeMode === "dark"
                  ? "2px solid #00d4ff"
                  : "1px solid var(--border-primary)",
              borderRadius: "8px",
              background: "var(--bg-secondary)",
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.2s ease",
            }}
          >
            🌙 Dark
          </div>
        </div>
        <small style={{ display: "block", marginTop: "8px", color: "var(--text-secondary)" }}>
          Auto follows your system preference
        </small>
      </div>

      {/* Account Actions */}
      <div className="setting-group" style={{ marginTop: "24px" }}>
        <label>Account</label>
        <button
          onClick={() => signOut()}
          style={{
            marginTop: "8px",
            width: "100%",
            padding: "14px",
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-primary)",
            borderRadius: "8px",
            color: "var(--text-primary)",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}
        >
          <span>👋 Sign Out</span>
        </button>
      </div>

      {/* GDPR Controls */}
      <div
        style={{
          marginTop: "48px",
          paddingTop: "24px",
          borderTop: "1px solid var(--border-primary)",
        }}
      >
        <h3>🔒 Privacy & Data</h3>

        {/* Export Data */}
        <div className="setting-group">
          <h4>Export Your Data</h4>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Download your data for backup or portability (GDPR compliant)
          </p>
          <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
            <button
              onClick={exportData}
              disabled={isExporting}
              title="Download all your data including conversations, messages, settings, and more"
              style={{
                padding: "10px 16px",
                background: "var(--accent-primary)",
                border: "none",
                borderRadius: "8px",
                color: "var(--text-primary)",
                fontSize: "14px",
                fontWeight: "500",
                cursor: isExporting ? "not-allowed" : "pointer",
                opacity: isExporting ? 0.6 : 1,
              }}
            >
              {isExporting ? "Exporting..." : "Export Everything (JSON)"}
            </button>
          </div>
        </div>

        {/* Archived Conversations */}
        <div className="setting-group" style={{ marginTop: "32px" }}>
          <h4>Archived Conversations</h4>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            View and manage conversations you've archived
          </p>

          {!showArchived ? (
            <button
              onClick={loadArchivedConversations}
              style={{
                marginTop: "12px",
                padding: "10px 16px",
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border-primary)",
                borderRadius: "8px",
                color: "var(--text-primary)",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              View Archived Conversations
            </button>
          ) : (
            <div style={{ marginTop: "12px" }}>
              {archivedConversations.length === 0 ? (
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                  No archived conversations found.
                </p>
              ) : (
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  {archivedConversations.map((conv) => (
                    <div
                      key={conv.id}
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid var(--border-primary)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: "var(--text-primary)" }}>{conv.title}</span>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => restoreConversation(conv.id)}
                          style={{
                            padding: "6px 12px",
                            background: "var(--accent-primary)",
                            border: "none",
                            borderRadius: "6px",
                            color: "var(--text-primary)",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => permanentlyDelete(conv.id)}
                          style={{
                            padding: "6px 12px",
                            background: "var(--accent-danger)",
                            border: "none",
                            borderRadius: "6px",
                            color: "var(--text-primary)",
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          Delete Forever
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Clear All Data */}
        <div className="setting-group" style={{ marginTop: "32px" }}>
          <h4>Clear All Data</h4>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Permanently remove all conversations and settings from the database
          </p>
          <button
            onClick={clearAllData}
            disabled={isDeleting}
            style={{
              marginTop: "12px",
              padding: "10px 16px",
              background: "var(--accent-danger)",
              border: "none",
              borderRadius: "8px",
              color: "var(--text-primary)",
              fontSize: "14px",
              fontWeight: "500",
              cursor: isDeleting ? "not-allowed" : "pointer",
              opacity: isDeleting ? 0.6 : 1,
            }}
          >
            {isDeleting ? "Deleting..." : "Clear All Data"}
          </button>
          <small
            style={{ display: "block", marginTop: "8px", color: "var(--accent-danger)" }}
          >
            ⚠️ This action cannot be undone
          </small>
        </div>
      </div>
    </div >
  );
};

export default GeneralTab;
